import React, { useState, useEffect } from "react";
import styled from "styled-components";
import i18n from 'i18next';
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { Upload } from 'antd';

import PageContentWrapper from "../components/core/PageContentWrapper";
import Container from "../components/common/Container";
import FormHeader from "../components/form/FormHeader";
import FormTitle from "../components/form/FormTitle";
import FormField from "../components/form/FormField";
import FormText from "../components/form/FormText";
import FormInput from "../components/form/FormInput";
import FormLabel from "../components/form/FormLabel";
import FormRemark from "../components/form/FormRemark";
import FormInputError from "../components/form/FormInputError";
import ShadowBox from "../components/core/ShadowBox";
import Space from "../components/core/Space";
import Flex from "../components/core/Flex";
import Divider from "../components/core/Divider";
import FormButtonPrimary from "../components/form/FormButtonPrimary";
import FormButtonSecondary from "../components/form/FormButtonSecondary";
import NavigationBarClean from "../components/common/NavigationBarClean";
import useAuthStore from "../store/useAuthStore";
import useApplySupplementaryStore from "../store/useApplySupplementaryStore";
import useModalStore from "../store/useModalStore";
import useLoadingStore from "../store/useLoadingStore";
import { basename, apiDomain, isProduction } from "../setting";
import withErrorHandling from "../function/withErrorHandling";
import NotFound from "./NotFound";
import feWriteLog from "../function/feWriteLog";

function ApplicationSupplementary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { emailId } = useParams();
  const { token, logout } = useAuthStore();
  const { openModalInfo } = useModalStore();
  const { fullname, getAttachIdByEmailId, attachid, document, addDocument, setDocument, documentError, isValid_emailId, submitSuppTempRecord, setDocumentError } = useApplySupplementaryStore();
  const { Dragger } = Upload;

  // useEffect(() => { document.title = t("translation:supplementary.document_title") }, [i18n.language]);

  const loadAttachIdByEmailId = withErrorHandling(getAttachIdByEmailId);

  useEffect(() => {
    loadAttachIdByEmailId(emailId);
  }, []);

  const props = {
    name: 'file',
    multiple: true,
    listType: 'picture',
    fileList: document,
    onChange: (info) => {
      // console.log("upload file", info.file)
    },
    beforeUpload: async (file, fileList) => {
      try {
        setDocumentError(false);
        const allowedFileTypes = ['image/png', 'image/jpeg', 'application/pdf', 'image/gif', 'image/tiff', 'image/tif'];
        const isAllowedFileType = allowedFileTypes.includes(file.type);
        if (!isAllowedFileType) {
          throw new Error('File Type Error');
        }

        const isFileSizeValid = file.size <= 20 * 1024 * 1024; // 20MB in bytes
        if (!isFileSizeValid) {
          throw new Error('File Size Error');
        }

        // console.log("upload file", file, fileList)
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachid', attachid);
        formData.append('module', 'PREAUTH');
        formData.append('emailId', emailId);

        const response_upload = await fetch(`${apiDomain}/${basename}/api/preAuthCore/UploadSuppTempRecord`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData,
        });

        if (response_upload.status === 401) { // Unauthorized
          logout();
          throw new Error('Unauthorized');
        }

        if (!response_upload.ok) {
          throw new Error('Network Error');
        }

        const data_upload = await response_upload.json();
        // console.log("uploaded file", data_upload)
        const { attachobjid, contenttype, ofilename } = data_upload.attachment;

        const response_download = await fetch(`${apiDomain}/${basename}/api/preAuthCore/downloadTmp/${attachobjid}?isthumb=squ&attachid=${attachid}&emailId=${emailId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });

        if (response_download.status === 401) { // Unauthorized
          logout();
          throw new Error('Unauthorized');
        }

        if (!response_download.ok) {
          throw new Error(`Network Error`);
        }

        const data_download = await response_download.json();
        // console.log(data_download);

        const response_downloadOrg = await fetch(`${apiDomain}/${basename}/api/preAuthCore/downloadTmp/${attachobjid}?isthumb=N&attachid=${attachid}&emailId=${emailId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          },
        });

        if (response_downloadOrg.status === 401) { // Unauthorized
          logout();
          throw new Error('Unauthorized');
        }

        if (!response_downloadOrg.ok) {
          throw new Error(`Network Error`);
        }

        const data_downloadOrg = await response_downloadOrg.json();

        addDocument({
          attachid: attachid,
          attachobjid: attachobjid,
          name: ofilename,
          type: contenttype,
          url: data_download,
          orgUrl: data_downloadOrg,
        });

      } catch (error) {
        feWriteLog(`Error: ${error.message}`);
        if (error.message === "Unauthorized") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_expiry" : "pre_auth_uat_error_expiry",
            title: t("error:unauthorized.title"),
            text: t("error:unauthorized.message")
          });
          // setStep("loading");
        } else if (error.message === "Network Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_network" : "pre_auth_uat_error_network",
            title: t("error:network.title"),
            text: t("error:network.message")
          });
        } else if (error.message === "File Type Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_file_type" : "pre_auth_uat_error_file_type",
            title: t("error:fileFormat.title"),
            text: t("error:fileFormat.message")
          });
        } else if (error.message === "File Size Error") {
          openModalInfo({
            id: isProduction ? "pre_auth_error_file_size" : "pre_auth_uat_error_file_size",
            title: t("error:fileSize.title"),
            text: t("error:fileSize.message")
          });
        } else {
          openModalInfo({
            id: isProduction ? "pre_auth_error_unknown" : "pre_auth_uat_error_unknown",
            title: t("error:unknown.title"),
            text: t("error:unknown.message")
          });
        }

        return false;
      }

      return false;
    },
    onPreview: (file) => {
      const link = document.createElement('a');
      link.href = file.orgUrl;
      link.download = file.name;
      link.click();
    },
    onRemove: (file) => {
      fetch(`${apiDomain}/${basename}/api/preAuthCore/deleteTmp/${file.attachobjid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          "emailId": emailId,
          "attachid": attachid,
        })
      });

      setDocument(document.filter(f => f.attachobjid !== file.attachobjid));
    },
  };

  return (
    <PageContentWrapper>

      <NavigationBarClean hideCloseBtn={true} />

      {
        isValid_emailId ?
          <Container>
            <FormHeader>{t("translation:supplementary.header")}</FormHeader>
            <FormText>{t("translation:supplementary.title", { key: fullname })}</FormText>
            <Space height={24} />

            <FormTitle>{t("translation:supplementary.section1.title")}</FormTitle>

            <Space />

            <div style={{ maxWidth: 500 }}>
              <Dragger {...props}>
                <p className="ant-upload-drag-icon">
                  <img src={`${process.env.PUBLIC_URL}/images/attachment.png`} alt="edit" />
                </p>
                <FormTitle>{t("clickToUpload")}</FormTitle>
                <Space />
                <FormRemark style={{ display: "block" }}>{t("uploadRule1")}</FormRemark>
                <FormRemark style={{ display: "block" }}>{t("uploadRule2")}</FormRemark>
              </Dragger>
            </div>

            <FormInputError show={documentError}>* {t("pleaseUpload", { key: t("translation:apply.step2_2.document") })}</FormInputError>

            <Space height={24} />

            <Divider toBottom={true} />

            <StepNavBarContainer>
              <Navigation>
                <FormButtonPrimary onClick={() => {
                  submitSuppTempRecord(emailId);
                }}>{t("submit")}</FormButtonPrimary>
              </Navigation>
            </StepNavBarContainer>
          </Container>
          :
          <Flex direction={"column"}>
            <StatusErrorImg src={`${process.env.PUBLIC_URL}/images/status_error.png`} alt="Loading Error" />
            <FormHeader style={{ marginBottom: 10 }}>{t("error:404NotFound.title")}</FormHeader>
            <FormText>{t("error:404NotFound.content1")}</FormText>
            <FormText>{t("error:404NotFound.content2")}</FormText>
            <FormText style={{ marginBottom: 40 }}>{t("error:404NotFound.content3")}</FormText>
            <FormButtonSecondary onClick={() => {
              navigate(`/${basename}`, { replace: true });
            }}>
              {t("backToHome")}
            </FormButtonSecondary>
          </Flex>
      }
    </PageContentWrapper>
  );
}

const StatusErrorImg = styled.img`
  width: 240px;
  height: 240px;
  margin-bottom: 64px;
`

const StepNavBarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
}`;

const Navigation = styled.div`
  display: flex;
  gap: 20px;
  justify-content: space-between;
`;

const Icon20 = styled.img`
  width: 20px;
  height: 20px;
`;

const TextGreen = styled(FormText)`
  color: var(--aia-bx-green-60);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

const TextBlue = styled(FormText)`
  color: var(--aia-bx-dark-blue);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

const TextRed = styled(FormText)`
  color: var(--aia-bx-red-1);
  font-weight: bold;
  font-size: 12px;
  line-height: 18px;
`;

export default ApplicationSupplementary;